import type { StatsResponse, SuggestionsResponse } from '@formency/shared';

function normaliseTopicLabel(raw: string): string {
  return raw
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[?!.,;:]+$/g, '');
}

export function buildCommandBarPlaceholders(
  stats?: StatsResponse,
  suggestionsData?: SuggestionsResponse
): string[] {
  // Keep the first two prompts static so the UI always has stable starters
  // before any dynamic stats/suggestions data finishes loading.
  const items: string[] = ['What should I study today?', "What's my strongest area?"];
  const seen = new Set(items.map((item) => item.toLowerCase()));
  const usedTopics = new Set<string>();
  let lastStem: string | null = null;

  const pushUnique = (item: string): boolean => {
    const value = item.trim();
    if (!value) return false;
    const key = value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    items.push(value);
    return true;
  };

  const addWithStems = (topic: string, templates: string[]) => {
    const cleanedTopic = normaliseTopicLabel(topic);
    if (!cleanedTopic) return false;
    const topicKey = cleanedTopic.toLowerCase();
    if (usedTopics.has(topicKey)) return false;

    for (const template of templates) {
      const stem = template.split('{topic}')[0].trim().toLowerCase();
      if (stem && lastStem === stem) continue;
      const candidate = template.replace('{topic}', cleanedTopic);
      if (pushUnique(candidate)) {
        lastStem = stem || null;
        usedTopics.add(topicKey);
        return true;
      }
    }

    return false;
  };

  const focusAreas = stats?.performance?.focusAreas ?? [];
  const focusTopics = focusAreas
    .map((area) => normaliseTopicLabel(area.displayName ?? area.tagName))
    .filter((title) => !!title);

  // Topics from homepage coach suggestions (aligned with widget).
  const suggestionTopics =
    suggestionsData?.suggestions
      ?.filter((s) => s.nudge !== 'start')
      .map((s) => normaliseTopicLabel(s.title))
      .filter((title) => !!title) ?? [];

  // Unified topic pool for command prompts, preserving order.
  const commandTopics = [...new Set([...suggestionTopics, ...focusTopics])];

  // ── Questions: library / performance only ───────────────────────────────
  pushUnique('How am I doing this week?');
  pushUnique("What's my weakest area?");
  for (const topic of focusTopics) {
    if (addWithStems(topic, ['How am I doing in {topic}?'])) break;
  }

  // ── Commands: revise / create / find content ────────────────────────────
  let reviseAdded = 0;
  for (const topic of focusTopics) {
    if (reviseAdded >= 2) break;
    if (addWithStems(topic, ['Revise {topic}', 'Start revising {topic}'])) {
      reviseAdded++;
    }
  }

  let createAdded = 0;
  for (const topic of commandTopics) {
    if (createAdded >= 2) break;
    if (addWithStems(topic, ['Create a lesson on {topic}', 'Build a lesson on {topic}'])) {
      createAdded++;
    }
  }

  // Generic command fallbacks only when no dynamic topics are available.
  if (commandTopics.length === 0) {
    pushUnique('Start a revision session');
    pushUnique('Create a lesson about time management');
    pushUnique('Open my latest course');
  }

  return items;
}
